import axios from "axios";
import { constant } from "common/constant";
import { getLoggedInUser, isActiveRoleEmployee } from "common/utils/roleUtils";

let refreshPromise = null;

const getTokenStorage = () => {
  if (localStorage.getItem("token")) return localStorage;
  if (sessionStorage.getItem("token")) return sessionStorage;
  if (localStorage.getItem(constant.refreshToken)) return localStorage;
  if (sessionStorage.getItem(constant.refreshToken)) return sessionStorage;
  return localStorage;
};

const getAccessToken = () =>
  localStorage.getItem("token") || sessionStorage.getItem("token");

const getRefreshToken = () =>
  localStorage.getItem(constant.refreshToken) || sessionStorage.getItem(constant.refreshToken);

const persistAuthData = (payload = {}, preferredStorage = null) => {
  const storage = preferredStorage || getTokenStorage();
  const otherStorage = storage === localStorage ? sessionStorage : localStorage;

  if (payload?.token) {
    storage.setItem("token", payload.token);
    otherStorage.removeItem("token");
  }

  if (payload?.refreshToken) {
    storage.setItem(constant.refreshToken, payload.refreshToken);
    otherStorage.removeItem(constant.refreshToken);
  }

  if (payload?.user) {
    localStorage.setItem("user", JSON.stringify(payload.user));
  }
};

const clearAuthData = () => {
  localStorage.removeItem("token");
  sessionStorage.removeItem("token");
  localStorage.removeItem(constant.refreshToken);
  sessionStorage.removeItem(constant.refreshToken);
  localStorage.removeItem("user");
};

const shouldAttemptRefresh = (error) => {
  const status = error?.response?.status;
  const message = String(error?.response?.data?.message || error?.message || "").toLowerCase();
  return status === 401 || message.includes("token") || message.includes("expired");
};

const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  const storage = getTokenStorage();
  const response = await axios.post(`${constant.baseUrl}api/user/refresh-token`, { refreshToken });
  persistAuthData(response?.data, storage);
  return response?.data?.token;
};

const getAuthHeaders = (extraHeaders = {}) => {
  const token = getAccessToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extraHeaders,
  };
};

const authenticatedRequest = async (config, allowRefresh = true) => {
  try {
    const response = await axios({
      ...config,
      headers: getAuthHeaders(config.headers),
    });
    persistAuthData(response?.data);
    return response;
  } catch (error) {
    if (!allowRefresh || !shouldAttemptRefresh(error) || config?.url?.includes("/api/user/refresh-token")) {
      throw error;
    }

    try {
      refreshPromise = refreshPromise || refreshAccessToken();
      const newToken = await refreshPromise;
      refreshPromise = null;

      const retryResponse = await axios({
        ...config,
        headers: {
          ...(config.headers || {}),
          Authorization: `Bearer ${newToken}`,
        },
      });
      persistAuthData(retryResponse?.data);
      return retryResponse;
    } catch (refreshError) {
      refreshPromise = null;
      clearAuthData();
      if (typeof window !== "undefined" && window.location.pathname !== "/auth/sign-in") {
        window.location.replace("/auth/sign-in");
      }
      throw refreshError;
    }
  }
};

export const postApi = async (path, data, login) => {
  try {
    const isLoginRequest = path === "api/user/login";
    if (isLoginRequest) {
      const storage = login ? localStorage : sessionStorage;
      const result = await axios.post(constant.baseUrl + path, data);
      persistAuthData(result?.data, storage);
      return result;
    }

    return await authenticatedRequest({
      method: "post",
      url: constant.baseUrl + path,
      data,
    });
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const putApi = async (path, data, id) => {
  try {
    return await authenticatedRequest({
      method: "put",
      url: constant.baseUrl + path,
      data,
    });
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const deleteApi = async (path, param) => {
  try {
    const url = constant.baseUrl + path + (param || '');
    return await authenticatedRequest({
      method: "delete",
      url,
    });
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const deleteManyApi = async (path, data) => {
  try {
    return await authenticatedRequest({
      method: "post",
      url: constant.baseUrl + path,
      data,
    });
  } catch (e) {
    console.error(e);
    throw e;
  }
};

// export const getApi = async (path, id) => {
//   try {
//     if (id) {
//       let result = await axios.get(constant.baseUrl + path + id, {
//         headers: {
//           Authorization: `Bearer ${
//             localStorage.getItem("token") || sessionStorage.getItem("token")
//             }`,
//         },
//       });
//       return result;
//     } else {
//       let result = await axios.get(constant.baseUrl + path, {
//         headers: {
//           Authorization: `Bearer ${
//             localStorage.getItem("token") || sessionStorage.getItem("token")
//             }`,
//         },
//       });
//       return result;
//     }
//   } catch (e) {
//     console.error(e);
//     throw e;
//   }
// };

export const getApi = async (path, id, responseType = "json") => {
  try {
    const rawPath = String(path || "");
    const isAllUsersQuery = /api\/user\/?\?[^#]*\ballData=true\b/i.test(rawPath);
    const isRoleAccessQuery = /api\/role-access\b/i.test(rawPath);
    const isActivePerfCyclesQuery =
      /api\/organization\/performance\/cycles\?[^#]*\bstatus=active\b/i.test(rawPath);
    if ((isAllUsersQuery || isRoleAccessQuery || isActivePerfCyclesQuery) && isActiveRoleEmployee(getLoggedInUser())) {
      return { status: 200, data: { data: [] } };
    }

    const url = id
      ? constant.baseUrl + path + id
      : constant.baseUrl + path;

    return await authenticatedRequest({
      method: "get",
      url,
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      },
      responseType: responseType,
    });
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const postApiMultipart = async (path, formData) => {
  try {
    return await authenticatedRequest({
      method: "post",
      url: constant.baseUrl + path,
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const patchApi = async (path, data) => {
  try {
    return await authenticatedRequest({
      method: "patch",
      url: constant.baseUrl + path,
      data,
    });
  } catch (e) {
    console.error(e);
    throw e;
  }
};
